<script>
import Chart from 'chart.js/auto';
import palette from '@inscribe/signal/dist/palette';
import { RISK_LEVEL } from '@/constants/riskLevels';
import { documentRiskLevelToReadableLabel } from '@/utils/documentReviewAgent';

let chartInstance;

export default {
  name: 'DocumentRiskLevelsByDay',
  props: {
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      palette,
      lowRiskColor: palette['light-success-80'],
      mediumRiskColor: palette['light-warning-80'],
      highRiskColor: palette['light-error-90'],
      notApplicableRiskColor: palette['light-neutral-80'],
    };
  },
  watch: {
    data: {
      immediate: true,
      handler: function () {
        if (chartInstance) {
          chartInstance.destroy();
          this.createChart();
        }
      },
    },
  },
  mounted() {
    this.createChart();
  },
  methods: {
    getFont() {
      return {
        color: palette['light-neutral-110'],
        font: {
          family: '"Inter", Arial, Helvetica, sans-serif',
          size: 12,
          weight: 400,
        },
      };
    },
    navigateToDocuments(date, riskLevelKey) {
      // Format date as YYYY-MM-DD
      const formattedDate = new Date(date).toISOString().split('T')[0];

      // Map stack name to risk level value
      let riskLevelValue = '';
      switch (riskLevelKey) {
        case 'low_risk':
          riskLevelValue = RISK_LEVEL.LOW;
          break;
        case 'medium_risk':
          riskLevelValue = RISK_LEVEL.MEDIUM;
          break;
        case 'high_risk':
          riskLevelValue = RISK_LEVEL.HIGH;
          break;
        case 'not_applicable_risk':
          riskLevelValue = RISK_LEVEL.NOT_APPLICABLE;
          break;
      }

      this.$router.push({
        path: '/documents',
        query: {
          start_date: formattedDate,
          end_date: formattedDate,
          risk_levels: [riskLevelValue],
          return_to: 'analytics',
        },
      });
    },
    createChart() {
      const chartCanvas = this.$refs.chart;
      if (!chartCanvas) {
        return;
      }

      chartInstance = new Chart(chartCanvas, {
        data: {
          labels: this.data.low_risk.map((d) => d.date),
          datasets: [
            {
              label: documentRiskLevelToReadableLabel(RISK_LEVEL.LOW),
              type: 'bar',
              data: this.data.low_risk,
              parsing: {
                xAxisKey: 'date',
                yAxisKey: 'percentage',
              },
              backgroundColor: this.lowRiskColor,
              borderRadius: 0,
              borderColor: this.lowRiskColor,
              barPercentage: 0.7,
              stack: 'low_risk',
            },
            {
              label: documentRiskLevelToReadableLabel(RISK_LEVEL.MEDIUM),
              type: 'bar',
              data: this.data.medium_risk,
              parsing: {
                xAxisKey: 'date',
                yAxisKey: 'percentage',
              },
              backgroundColor: this.mediumRiskColor,
              borderRadius: 0,
              borderColor: this.mediumRiskColor,
              barPercentage: 0.7,
              stack: 'medium_risk',
            },
            {
              label: documentRiskLevelToReadableLabel(RISK_LEVEL.HIGH),
              type: 'bar',
              data: this.data.high_risk,
              key: 'high_risk',
              parsing: {
                xAxisKey: 'date',
                yAxisKey: 'percentage',
              },
              backgroundColor: this.highRiskColor,
              borderRadius: 0,
              borderColor: this.highRiskColor,
              barPercentage: 0.7,
              stack: 'high_risk',
            },
            {
              label: documentRiskLevelToReadableLabel(
                RISK_LEVEL.NOT_APPLICABLE,
              ),
              type: 'bar',
              data: this.data.not_applicable_risk,
              parsing: {
                xAxisKey: 'date',
                yAxisKey: 'percentage',
              },
              backgroundColor: this.notApplicableRiskColor,
              borderRadius: 0,
              borderColor: this.notApplicableRiskColor,
              barPercentage: 0.7,
              stack: 'not_applicable_risk',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          onHover: (event, elements) => {
            event.native.target.style.cursor = elements.length
              ? 'pointer'
              : 'default';
          },
          onClick: (_evt, elements) => {
            if (elements.length > 0) {
              const { index, datasetIndex } = elements[0];

              const date = chartInstance.data.labels[index];
              const riskLevelKey =
                chartInstance.data.datasets[datasetIndex].stack;

              this.navigateToDocuments(date, riskLevelKey);
            }
          },
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                ...this.getFont(),
                boxWidth: 12,
                padding: 15,
              },
            },
            tooltip: {
              displayColors: true,
              callbacks: {
                title(tooltipItems) {
                  return new Date(tooltipItems[0].label).toLocaleDateString();
                },
                label(context) {
                  const { dataIndex, dataset } = context;
                  const { data, stack } = dataset;
                  const { percentage, num_docs, total_docs } = data[dataIndex];

                  let formattedRiskLevel = '';
                  switch (stack) {
                    case 'low_risk':
                      formattedRiskLevel = documentRiskLevelToReadableLabel(
                        RISK_LEVEL.LOW,
                      );
                      break;
                    case 'medium_risk':
                      formattedRiskLevel = documentRiskLevelToReadableLabel(
                        RISK_LEVEL.MEDIUM,
                      );
                      break;
                    case 'high_risk':
                      formattedRiskLevel = documentRiskLevelToReadableLabel(
                        RISK_LEVEL.HIGH,
                      );
                      break;
                    default:
                      formattedRiskLevel = documentRiskLevelToReadableLabel(
                        RISK_LEVEL.NOT_APPLICABLE,
                      );
                  }

                  return `${percentage}% ${formattedRiskLevel} (${num_docs} of ${total_docs} documents)`;
                },
              },
            },
          },
          scales: {
            x: {
              title: {
                display: false,
                ...this.getFont(),
              },
              display: true,
              grid: {
                display: false,
              },
              type: 'time',
              ticks: {
                callback: (value) => new Date(value).toLocaleDateString(),
                source: 'data',
                autoSkip: true,
                ...this.getFont(),
              },
            },
            y: {
              beginAtZero: true,
              min: 0,
              max: 100,
              title: {
                display: true,
                text: '% of Documents',
                ...this.getFont(),
              },
              display: true,
              grid: {
                display: true,
              },
              ticks: {
                stepSize: 25,
                ...this.getFont(),
              },
            },
          },
        },
      });
    },
  },
};
</script>

<template>
  <div
    v-if="data?.low_risk?.length"
    class="document-risk-levels-by-day d-flex flex-row align-items-center justify-content-center"
  >
    <canvas ref="chart"></canvas>
  </div>
</template>

<style lang="scss" scoped>
.document-risk-levels-by-day {
  width: 100%;

  canvas {
    width: 100%;
    height: 200px;
  }
}
</style>
